import { createContext, useCallback, useContext, type ReactNode } from 'react';
import { Link, useNavigate, type LinkProps } from 'react-router';
import type { RouteParams, RouteRegistry } from './registry';

const RouteRegistryContext = createContext<RouteRegistry | undefined>(undefined);

export function RouteRegistryProvider({
  registry,
  children,
}: {
  registry: RouteRegistry;
  children: ReactNode;
}) {
  return <RouteRegistryContext.Provider value={registry}>{children}</RouteRegistryContext.Provider>;
}

export function useRouteRegistry(): RouteRegistry {
  const registry = useContext(RouteRegistryContext);
  if (!registry) throw new Error('useRouteRegistry must be used inside RouteRegistryProvider.');
  return registry;
}

/**
 * The route helper. Modules link to any screen, their own or another module's, by route id only, never by a
 * hard-coded path, because the shell decides where each module is mounted (ADR-0003).
 */
export function useRouteHref(): (routeId: string, params?: RouteParams) => string | null {
  return useRouteRegistry().href;
}

/** Navigates to a route id. A route this shell does not compose is a no-op. */
export function useNavigateTo(): (routeId: string, params?: RouteParams) => void {
  const href = useRouteHref();
  const navigate = useNavigate();
  return useCallback(
    (routeId: string, params?: RouteParams) => {
      const path = href(routeId, params);
      if (path) void navigate(path);
    },
    [href, navigate],
  );
}

/**
 * A link to a route id. Renders its children without a link when this shell does not compose the target, so a
 * module still works in a shell that leaves the other module out.
 */
export function RouteLink({
  to,
  params,
  search,
  children,
  ...props
}: Omit<LinkProps, 'to'> & { to: string; params?: RouteParams; search?: string }) {
  const path = useRouteHref()(to, params);
  if (!path)
    return (
      <span className={typeof props.className === 'string' ? props.className : undefined}>{children}</span>
    );
  return (
    <Link to={search ? `${path}?${search}` : path} {...props}>
      {children}
    </Link>
  );
}
